import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ReactComponent as BaseFloodElevationGraphic } from '../assets/base_flood_elevation.svg';
import { Modal } from './general/Modal';

const ACCEPTED_FFE_FILE_EXTENSIONS = ['.pdf', '.txt', '.jpg', '.jpeg', '.png'];
const ACCEPTED_FFE_FILE_TYPES = ['application/pdf', 'text/plain', 'image/jpeg', 'image/png'];

const hasValue = value => value !== null && value !== undefined && value !== '';
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const DEFAULT_BFE = 5;
const SVG_HEIGHT = 846;
const WATER_TOP_DEFAULT_Y = 577;
const WATER_TOP_MIN_Y = 36;
const WATER_LEFT_X = 0;
const WATER_RIGHT_X = 1212;
const GROUND_Y = 654;
const STATIC_POST_TOP_DEFAULT_Y = 543;
const STATIC_POST_BOTTOM_Y = 656;
const STATIC_POST_SCALE_MULTIPLIER = 1.7;
const MIN_ELEVATION_FT = 0;
const MAX_ELEVATION_FT = 100;
const BASE_FLOOD_VISUAL_CAP_RATIO = 0.52;
const residenceStatusPillClass = {
  above: 'bg-low text-rain',
  at: 'bg-sunshine text-rain',
  below: 'bg-whoop-200 text-whoop',
  unknown: 'bg-gray-100 text-gray-700',
};

const formatFreeboard = value => {
  if (!Number.isFinite(value)) {
    return '0.0 ft';
  }
  if (value > 0) {
    return `+${value.toFixed(1)} ft`;
  }
  return `${value.toFixed(1)} ft`;
};

const normalizeElevation = value => {
  const safeValue = Number.isFinite(value) ? value : MIN_ELEVATION_FT;
  return clamp((safeValue - MIN_ELEVATION_FT) / (MAX_ELEVATION_FT - MIN_ELEVATION_FT), 0, 1);
};

const hasAcceptedExtension = fileName =>
  ACCEPTED_FFE_FILE_EXTENSIONS.some(extension => fileName.toLowerCase().endsWith(extension));

const isAcceptedFfeFile = file => {
  if (!file) {
    return false;
  }
  return ACCEPTED_FFE_FILE_TYPES.includes(file.type) || hasAcceptedExtension(file.name);
};

const InfoTooltip = ({ label, text }) => (
  <span className="group relative inline-flex items-center">
    <button
      type="button"
      aria-label={`More info about ${label}`}
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-fossil text-[10px] font-bold leading-none text-onyx"
    >
      ?
    </button>
    <span className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 hidden w-56 -translate-x-1/2 rounded bg-rain px-2 py-1 text-[11px] leading-snug text-white shadow-sm group-focus-within:block group-hover:block">
      {text}
    </span>
  </span>
);

const BaseFloodElevationModal = ({
  isOpen,
  onClose,
  initialValue,
  firstFloorElevation = null,
  ecFlag = null,
  onSave,
}) => {
  const ffeFileInputRef = useRef(null);
  const [ffeUploadFileName, setFfeUploadFileName] = useState('');
  const [ffeUploadFile, setFfeUploadFile] = useState(null);
  const [ffeUploadError, setFfeUploadError] = useState('');
  const [draftValue, setDraftValue] = useState(
    hasValue(initialValue) ? String(initialValue) : String(DEFAULT_BFE)
  );
  const [draftFfe, setDraftFfe] = useState(
    hasValue(firstFloorElevation)
      ? String(firstFloorElevation)
      : hasValue(initialValue)
      ? String(initialValue)
      : String(DEFAULT_BFE)
  );

  useEffect(() => {
    if (isOpen) {
      const nextBfe = hasValue(initialValue) ? Number(initialValue) : DEFAULT_BFE;
      setDraftValue(String(nextBfe));
      const nextFfe = hasValue(firstFloorElevation) ? Number(firstFloorElevation) : nextBfe;
      setDraftFfe(String(nextFfe));
      setFfeUploadFileName('');
      setFfeUploadFile(null);
      setFfeUploadError('');
    }
  }, [isOpen, initialValue, firstFloorElevation]);

  const numericBfe = Number(draftValue);
  const numericFfe = Number(draftFfe);
  const hasNumericBfe = Number.isFinite(numericBfe) && numericBfe >= 0;
  const hasNumericFfe = Number.isFinite(numericFfe) && numericFfe >= 0;
  const derivedFreeboard = hasNumericFfe && hasNumericBfe ? numericFfe - numericBfe : 0;
  const normalizedBfe = normalizeElevation(numericBfe) * BASE_FLOOD_VISUAL_CAP_RATIO;
  const normalizedFfe = normalizeElevation(numericFfe) * BASE_FLOOD_VISUAL_CAP_RATIO;
  const computedWaterTopY = hasNumericBfe
    ? clamp(GROUND_Y - normalizedBfe * (GROUND_Y - WATER_TOP_MIN_Y), WATER_TOP_MIN_Y, GROUND_Y)
    : WATER_TOP_DEFAULT_Y;
  const bfeLabelTopPercent = (computedWaterTopY / SVG_HEIGHT) * 100;
  // Home elevation follows FFE while water level follows BFE.
  const baseBottomBarThickness = 24;
  const baseBottomBaselineY = 603;
  const bodyBottomBaselineY = 535.33;
  const mappedFirstFloorElevationY = hasNumericFfe
    ? clamp(GROUND_Y - normalizedFfe * (GROUND_Y - WATER_TOP_MIN_Y), WATER_TOP_MIN_Y, GROUND_Y)
    : GROUND_Y;
  const baseBottomTargetY = hasNumericFfe
    ? clamp(mappedFirstFloorElevationY + baseBottomBarThickness, 260, 794)
    : baseBottomBaselineY;
  const baseOffsetY = clamp(baseBottomTargetY - baseBottomBaselineY, -160, 200);
  const bodyOffsetY = clamp(baseBottomTargetY - bodyBottomBaselineY, -200, 220);
  const staticPostTopY = clamp(
    STATIC_POST_TOP_DEFAULT_Y + baseOffsetY * STATIC_POST_SCALE_MULTIPLIER,
    220,
    646
  );
  const firstFloorElevationY = clamp(baseBottomTargetY - baseBottomBarThickness, 220, 794);
  const firstFloorLabelTopPercent = (firstFloorElevationY / SVG_HEIGHT) * 100;
  const residenceStatusType =
    !hasNumericFfe || !hasNumericBfe
      ? 'unknown'
      : derivedFreeboard > 0
      ? 'above'
      : derivedFreeboard < 0
      ? 'below'
      : 'at';
  const freeboardPillText = formatFreeboard(derivedFreeboard);

  const handleUploadFfeDocumentClick = () => {
    ffeFileInputRef.current?.click();
  };

  const handleFfeDocumentSelected = event => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      setFfeUploadFileName('');
      setFfeUploadError('');
      return;
    }

    if (!isAcceptedFfeFile(selectedFile)) {
      setFfeUploadFileName('');
      setFfeUploadError('Please upload a PDF, TXT, JPG, JPEG, or PNG file.');
      if (ffeFileInputRef.current) {
        ffeFileInputRef.current.value = '';
      }
      return;
    }

    setFfeUploadFileName(selectedFile.name);
    setFfeUploadFile(selectedFile);
    setFfeUploadError('');
  };

  // Save commits the certificate only, so it stays disabled until one is attached.
  const canSaveCertificate = Boolean(ffeUploadFile);

  const handleSave = () => {
    if (!canSaveCertificate) {
      return;
    }
    onSave({
      baseFloodElevation: hasValue(draftValue) ? Number(draftValue) : DEFAULT_BFE,
      firstFloorElevation: hasValue(draftFfe)
        ? Number(draftFfe)
        : Number(draftValue || DEFAULT_BFE),
      certificateFileInfo: ffeUploadFile,
    });
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const svg = document.getElementById('base-flood-elevation-graphic');
    if (!svg) {
      return;
    }

    const queryByIds = (...ids) => ids.map(id => svg.querySelector(`#${id}`)).find(Boolean);
    const water = queryByIds('water');
    const topLine = queryByIds('top_line', 'top-line');
    const firstFloorElevationLine = queryByIds('first_floor_elevation', 'first-floor-elevation');
    const freeboardPointerLine = queryByIds('freeboard_pointer_line', 'freeboard-pointer-line');
    const freeboardPointerHead = queryByIds('freeboard_pointer_head', 'freeboard-pointer-head');
    const homeBody = queryByIds('home_body', 'home-body');
    const homeBaseAndSides = queryByIds('home_base_and_sides', 'home-base-and-sides');
    const homeStaticPosts = queryByIds('home_static_posts', 'home-static-posts');
    const leftArrow = queryByIds('left_arrow', 'left-arrow');

    if (water) {
      water.setAttribute(
        'd',
        `M${WATER_RIGHT_X} ${computedWaterTopY}H${WATER_LEFT_X}V778H${WATER_RIGHT_X}V${computedWaterTopY}Z`
      );
    }
    if (topLine) {
      topLine.setAttribute('d', `M${WATER_LEFT_X} ${computedWaterTopY}H${WATER_RIGHT_X}`);
    }
    if (firstFloorElevationLine) {
      firstFloorElevationLine.setAttribute('d', `M760 ${firstFloorElevationY}H889`);
    }
    const freeboardIndicatorY = firstFloorElevationY;
    if (freeboardPointerLine) {
      freeboardPointerLine.setAttribute('d', `M860 ${freeboardIndicatorY}H800`);
    }
    if (freeboardPointerHead) {
      freeboardPointerHead.setAttribute(
        'd',
        `M778 ${freeboardIndicatorY}L802 ${freeboardIndicatorY - 12}V${
          freeboardIndicatorY + 12
        }L778 ${freeboardIndicatorY}Z`
      );
    }
    if (homeBaseAndSides) {
      homeBaseAndSides.setAttribute('transform', `translate(0 ${baseOffsetY})`);
    }
    if (homeBody) {
      homeBody.setAttribute('transform', `translate(0 ${bodyOffsetY})`);
    }
    if (homeStaticPosts) {
      const [leftPost, rightPost] = homeStaticPosts.querySelectorAll('path');
      if (leftPost) {
        leftPost.setAttribute(
          'd',
          `M456 ${STATIC_POST_BOTTOM_Y}L456 ${staticPostTopY}L432 ${staticPostTopY}L432 ${STATIC_POST_BOTTOM_Y}Z`
        );
      }
      if (rightPost) {
        rightPost.setAttribute(
          'd',
          `M761 ${STATIC_POST_BOTTOM_Y}L761 ${staticPostTopY}L737 ${staticPostTopY}L737 ${STATIC_POST_BOTTOM_Y}Z`
        );
      }
    }
    if (leftArrow) {
      const waterDeltaY = computedWaterTopY - WATER_TOP_DEFAULT_Y;
      leftArrow.setAttribute('transform', `translate(0 ${waterDeltaY})`);
    }
  }, [baseOffsetY, bodyOffsetY, computedWaterTopY, firstFloorElevationY, isOpen, staticPostTopY]);

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <Modal
      onClose={onClose}
      closeOnOuterClick={false}
      wrapperStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.68)' }}
      style={{ width: '90%', maxWidth: '760px', padding: '20px 24px' }}
    >
      <div className="mx-auto max-w-[700px] text-left text-rain">
        <div className="flex items-center gap-3">
          <h3 className="font-condensed text-2xl font-bold">Base Flood Elevation (BFE)</h3>
          {ecFlag === true ? (
            <span className="rounded bg-low px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rain">
              Elevation Certificate on File
            </span>
          ) : ecFlag === false ? (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              No Elevation Certificate
            </span>
          ) : null}
        </div>
        <p className="mt-2 text-sm text-onyx">
          Adjust the values below to preview how this property sits against the base flood
          elevation. Preview changes are not saved.
        </p>

        <div className="my-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="rounded border border-fossil bg-white p-2 text-sm md:col-span-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-onyx">Freeboard</div>
            <div
              className={`mt-2 inline-flex rounded-full px-3 py-1 text-sm font-bold ${residenceStatusPillClass[residenceStatusType]}`}
            >
              {freeboardPillText}
            </div>
          </div>
          <div className="rounded border border-fossil bg-white p-3 md:col-span-3">
            <div className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
              <label>Base Flood Elevation (ft)</label>
              <InfoTooltip
                label="Base Flood Elevation"
                text="The water-surface elevation FEMA uses as the reference flood level for this property."
              />
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              value={draftValue}
              onChange={e => setDraftValue(e.target.value)}
              placeholder="e.g. 12.5"
              className="w-full border border-fossil px-2 py-2 text-sm"
            />
            <input
              type="range"
              min="0"
              max={MAX_ELEVATION_FT}
              step="0.1"
              value={hasValue(draftValue) ? Number(draftValue) : DEFAULT_BFE}
              onChange={e => setDraftValue(e.target.value)}
              className="mt-1 w-full"
            />

            <div className="mb-1 mt-3 flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
              <label>Finished Floor Elevation (ft)</label>
              <InfoTooltip
                label="Finished Floor Elevation"
                text="The elevation of the lowest finished livable floor, compared against BFE."
              />
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              value={draftFfe}
              onChange={e => setDraftFfe(e.target.value)}
              placeholder="e.g. 8.0"
              className="w-full border border-fossil px-2 py-2 text-sm"
            />
            <input
              type="range"
              min="0"
              max={MAX_ELEVATION_FT}
              step="0.1"
              value={hasValue(draftFfe) ? Number(draftFfe) : Number(draftValue || DEFAULT_BFE)}
              onChange={e => setDraftFfe(e.target.value)}
              className="mt-1 w-full"
            />
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-2 grid grid-cols-1 gap-2 md:hidden">
            <div className="w-full rounded bg-white px-2 py-1 text-center text-[10px] font-bold leading-tight text-rain shadow-sm">
              Base Flood Elevation (ft) (
              {hasValue(draftValue) ? `${draftValue} ft` : `${DEFAULT_BFE} ft`})
            </div>
            <div className="w-full rounded bg-white px-2 py-1 text-center text-[10px] font-bold leading-tight text-rain shadow-sm">
              Finished Floor Elevation (ft) (
              {hasValue(draftFfe) ? `${draftFfe} ft` : `${DEFAULT_BFE} ft`})
            </div>
          </div>
          <div className="relative flex w-full justify-center border border-fossil bg-snow p-3">
            <div className="pointer-events-none absolute inset-0 hidden md:block">
              <div
                className="absolute left-[45px] w-[96px] whitespace-normal rounded bg-white px-1 text-center text-[10px] font-bold leading-tight text-rain shadow-sm"
                style={{ top: `calc(${bfeLabelTopPercent}% - 15px)` }}
              >
                Base Flood Elevation (ft) (
                {hasValue(draftValue) ? `${draftValue} ft` : `${DEFAULT_BFE} ft`})
              </div>
              <div
                className="absolute right-[94px] w-[86px] whitespace-normal rounded bg-white px-1 text-center text-[10px] font-bold leading-tight text-rain shadow-sm"
                style={{ top: `calc(${firstFloorLabelTopPercent}% - 17px)` }}
              >
                Finished Floor Elevation (ft) (
                {hasValue(draftFfe) ? `${draftFfe} ft` : `${DEFAULT_BFE} ft`})
              </div>
            </div>
            <BaseFloodElevationGraphic
              id="base-flood-elevation-graphic"
              aria-label="Graphic of residence below, at, and above base flood elevation"
              className="max-h-[260px] w-full bg-[#f0f3f5]"
            />
          </div>
        </div>

        <div className="mb-4 rounded border border-fossil bg-white p-3">
          <div className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
            <label>Elevation Certificate (optional)</label>
            <InfoTooltip
              label="Elevation Certificate"
              text="A FEMA Elevation Certificate documenting this property's finished floor elevation."
            />
          </div>
          <p className="mb-2 text-xs text-onyx">
            Attach a document to save it to this property. Accepts PDF, TXT, JPG, JPEG, or PNG. This
            is the only part of this dialog that is saved.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={ffeFileInputRef}
              type="file"
              className="hidden"
              accept={ACCEPTED_FFE_FILE_EXTENSIONS.join(',')}
              onChange={handleFfeDocumentSelected}
            />
            <button
              type="button"
              className="border border-fossil bg-white px-3 py-1 text-sm font-bold text-rain hover:bg-snow"
              onClick={handleUploadFfeDocumentClick}
            >
              Upload FFE Document
            </button>
            {ffeUploadFileName ? (
              <span className="max-w-[220px] truncate text-xs font-bold text-onyx">
                {ffeUploadFileName}
              </span>
            ) : (
              <span className="text-xs text-onyx">No document attached</span>
            )}
          </div>
          {ffeUploadError && <p className="mt-2 text-xs text-whoop">{ffeUploadError}</p>}
        </div>

        <div className="mt-4 flex flex-col gap-2 border-t border-fossil pt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-onyx">
            {canSaveCertificate ? `Saving uploads ${ffeUploadFileName} to this property.` : ''}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="border border-fossil px-3 py-1 text-sm"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              disabled={!canSaveCertificate}
              title={canSaveCertificate ? undefined : 'Upload an elevation certificate to save it.'}
              className="bg-rain px-3 py-1 text-sm font-bold text-white disabled:pointer-events-none disabled:cursor-auto disabled:opacity-50"
              onClick={handleSave}
            >
              Save Certificate
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );

  return createPortal(modalContent, document.body);
};

export default BaseFloodElevationModal;
